// ============================================================
// FIXED: SalesByPaymentReportPage.tsx
// ============================================================
// src/features/reports/pages/SalesByPaymentReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getSalesByPaymentMethod, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    CreditCard, Wallet, Banknote, Smartphone,
    Building2, DollarSign, Percent, PieChart
} from 'lucide-react';
import { motion } from 'framer-motion';

const paymentIcons: Record<string, React.ElementType> = {
    cash: Banknote,
    bank_transfer: Building2,
    pos_card: CreditCard,
    mobile_money: Smartphone,
    cheque: Wallet,
};

const paymentColors: Record<string, string> = {
    cash: 'from-emerald-500 to-teal-600',
    bank_transfer: 'from-blue-500 to-indigo-600',
    pos_card: 'from-purple-500 to-violet-600',
    mobile_money: 'from-amber-500 to-orange-600',
    cheque: 'from-rose-500 to-pink-600',
};

export function SalesByPaymentReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-sales-by-payment', tenantId, params],
        queryFn: () => getSalesByPaymentMethod(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'sales_by_payment', format, params as any);
        window.open(url, '_blank');
    };

    // FIXED: Proper boolean conversion
    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);

    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const methods = data?.methods || [];
    const totalAmount = parseFloat(data?.summary?.totalAmount || '0');

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            <ReportHeader
                title="Sales by Payment Method"
                description="Payment method distribution"
                icon={CreditCard}
                iconGradient="from-blue-500 to-indigo-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || methods.length === 0 ? (
                    <EmptyState
                        icon={<CreditCard className="w-10 h-10" />}
                        title="No Payment Data"
                        description="No payment method data found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <SummaryCard
                                label="Total Collected"
                                value={formatNaira(data.summary?.totalAmount || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Payment Methods"
                                value={String(data.summary?.totalMethods || 0)}
                                icon={PieChart}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Transactions"
                                value={methods.reduce((s: number, m: any) => s + m.transactions, 0).toLocaleString()}
                                icon={CreditCard}
                                gradient="from-purple-500 to-violet-600"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {methods.map((method: any, i: number) => {
                                const Icon = paymentIcons[method.method] || CreditCard;
                                const gradient = paymentColors[method.method] || 'from-slate-500 to-slate-600';
                                const percentage = parseFloat(method.percentage || '0');

                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                    >
                                        <Card hover>
                                            <CardContent className="p-5">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient}`}>
                                                        <Icon className="w-5 h-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-800 capitalize">
                                                            {method.method.replace(/_/g, ' ')}
                                                        </p>
                                                        <p className="text-xs text-slate-400">{method.transactions} transactions</p>
                                                    </div>
                                                </div>
                                                <p className="text-2xl font-bold text-slate-900">{formatNaira(method.amount)}</p>
                                                <div className="mt-2 w-full bg-slate-100 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full bg-gradient-to-r ${gradient}`}
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">{percentage}% of total</p>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>

                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800">Payment Method Details</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Method</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Transactions</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Share</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Avg. Transaction</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {methods.map((method: any, i: number) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        {(() => {
                                                            const Icon = paymentIcons[method.method] || CreditCard;
                                                            const gradient = paymentColors[method.method] || 'from-slate-500 to-slate-600';
                                                            return (
                                                                <div className={`p-2 rounded-lg bg-gradient-to-br ${gradient}`}>
                                                                    <Icon className="w-4 h-4 text-white" />
                                                                </div>
                                                            );
                                                        })()}
                                                        <span className="text-sm font-medium text-slate-900 capitalize">
                                                            {method.method.replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm">
                                                    {method.transactions}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                    {formatNaira(method.amount)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge variant="info" size="sm">{method.percentage}%</Badge>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {formatNaira(method.transactions > 0 ? parseFloat(method.amount) / method.transactions : 0)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}